import React from 'react'
import { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Tab } from '../../../hooks/multiWindow'
import { DailyIcon } from '../../../components/icons/DailyIcon'
import { TopicsIcon } from '../../../components/icons/TopicsIcon'
import { SidebarSection } from './SidebarSection'
import { SidebarItem } from './SidebarItem'

interface AllSpacesSidebarContentProps {
  activeTab?: Tab
  openTab: (tab: Tab) => void
  navigateActiveTab: (updates: { path: string; title: string; icon?: LucideIcon }) => void
  tabs?: Tab[]
  onCloseTabByPath?: (path: string) => void
  onUnpinByPath?: (path: string) => void
  onPinByPath?: (path: string) => void
  openPreviewTab?: (tab: Omit<Tab, 'id'> & { id?: string }) => void
  promotePreviewBySpace?: (lifeSpace: string) => void
}

export const AllSpacesSidebarContent: React.FC<AllSpacesSidebarContentProps> = ({ activeTab, openTab, navigateActiveTab, tabs = [], onCloseTabByPath, onUnpinByPath, onPinByPath, openPreviewTab, promotePreviewBySpace }) => {
  const { t } = useTranslation()
  const openPaths = new Set(tabs.map(t => t.path))
  const previewPaths = new Set(tabs.filter(t => t.isPreview).map(t => t.path))
  const pinnedPaths = new Set(tabs.filter(t => t.isPinned).map(t => t.path))
  // If a path also has a non-pinned tab, don't show it as pinned
  tabs.forEach(t => { if (!t.isPinned) pinnedPaths.delete(t.path) })
  const handleOpenTab = (e: React.MouseEvent, id: string, path: string, title: string, icon?: LucideIcon) => {
    if (e.metaKey || e.ctrlKey) {
      openTab({
        id: `${id}-${Date.now()}`,
        path,
        title,
        icon,
        lifeSpace: 'all',
        isDirty: false,
        isPinned: false
      })
    } else if (openPreviewTab) {
      openPreviewTab({ path, title, icon, lifeSpace: 'all' })
    } else {
      navigateActiveTab({ path, title, icon })
    }
  }

  const handlePromote = promotePreviewBySpace ? () => promotePreviewBySpace('all') : undefined

  const handleDoubleClick = (path: string) => {
    if (previewPaths.has(path)) {
      promotePreviewBySpace?.('all')
    } else if (!openPaths.has(path)) {
      promotePreviewBySpace?.('all')
    }
  }

  return (
    <>
      <SidebarSection title={t('sidebar.allSpaces')} defaultExpanded>
      <SidebarItem
        icon={DailyIcon}
        label={t('sidebar.myDaily')}
        path="/home/overview"
        isActive={activeTab?.path === '/home/overview'}
        hasOpenTab={openPaths.has('/home/overview')}
        isPreview={previewPaths.has('/home/overview')}
        isPinned={pinnedPaths.has('/home/overview')}
        onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/overview') : undefined}
        onPinTab={onPinByPath ? () => onPinByPath('/home/overview') : undefined}
        onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/overview') : undefined}
        onOpenNewTab={() => openTab({ id: `all-daily-${Date.now()}`, path: '/home/overview', title: t('sidebar.myDaily'), icon: DailyIcon as LucideIcon, lifeSpace: 'all', isDirty: false, isPinned: false })}
        onClick={(e) => handleOpenTab(
          e,
          'all-daily',
          '/home/overview',
          t('sidebar.myDaily'),
          DailyIcon as LucideIcon
        )}
        onDoubleClick={() => handleDoubleClick('/home/overview')}
        onPromote={handlePromote}
      />
      <SidebarItem
        icon={TopicsIcon}
        label={t('sidebar.myTopics')}
        path="/home/all/topics"
        isActive={activeTab?.path === '/home/all/topics'}
        hasOpenTab={openPaths.has('/home/all/topics')}
        isPreview={previewPaths.has('/home/all/topics')}
        isPinned={pinnedPaths.has('/home/all/topics')}
        onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/all/topics') : undefined}
        onPinTab={onPinByPath ? () => onPinByPath('/home/all/topics') : undefined}
        onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/all/topics') : undefined}
        onOpenNewTab={() => openTab({ id: `all-topics-${Date.now()}`, path: '/home/all/topics', title: t('sidebar.myTopics'), icon: TopicsIcon as LucideIcon, lifeSpace: 'all', isDirty: false, isPinned: false })}
        onClick={(e) => handleOpenTab(
          e,
          'all-topics',
          '/home/all/topics',
          t('sidebar.myTopics'),
          TopicsIcon as LucideIcon
        )}
        onDoubleClick={() => handleDoubleClick('/home/all/topics')}
        onPromote={handlePromote}
      />
      </SidebarSection>
    </>
  )
}
